import {
  Box,
  Button,
  Dialog,
  Container,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
  Paper,
  ListItem,
  ListItemText,
  MenuList,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
  Divider,
} from '@mui/material';
import { useEffect, useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { getPrimaryCategories } from '../../utilities/Categories';
import { useTranslation } from 'react-i18next';
import {
  JaaRoute,
  getRouterPathToContentIndex,
  externalLegalInsightsLinks,
} from '../../utilities/Links';
import { Link as RouterLink, useLocation } from 'react-router';

const listItemStyles = {
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: '150%',
  letterSpacing: '0.15px',
  color: 'text.primary',
  paddingY: 0.5,
};

interface NavListProps {
  readonly handleClose: () => void;
}

export function NavList({ handleClose }: NavListProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const mainPages = [
    {
      name: t('frontPage.name'),
      path: JaaRoute.frontPage,
    },
    {
      name: t('content.indexTitle'),
      path: JaaRoute.contentIndex,
    },
    {
      name: t('news.title'),
      path: JaaRoute.newsIndex,
    },
    {
      name: t('authors.title'),
      path: JaaRoute.authorListPage,
    },
    {
      name: t('regulationFeed.titleLong'),
      path: JaaRoute.regulationIndex,
    },
  ];

  const articleLinks = [
    ...getPrimaryCategories().map((category) => ({
      name: category,
      path: getRouterPathToContentIndex(category),
    })),
  ];

  const sections = [
    { name: t('service.title'), list: mainPages },
    {
      name: t('header.menu.contentByCategory'),
      list: articleLinks,
    },
  ];

  return (
    <Grid
      container
      rowSpacing={0}
      columnSpacing={{ xs: 0, md: 6 }}
      sx={{
        height: '100%',
      }}
    >
      {sections.map((section) => {
        return (
          <Grid
            key={'nav-grid-section-' + section.name}
            size={{ xs: 12, md: 4 }}
          >
            <MenuList
              sx={{
                paddingTop: isMobile ? 2 : 0,
                paddingBottom: isMobile ? 3 : 0,
              }}
              subheader={
                <Typography
                  variant='h3'
                  sx={{
                    paddingX: isMobile ? 2 : 0,
                    paddingBottom: isMobile ? 1 : 2,
                    paddingTop: isMobile ? 1 : 0,
                  }}
                >
                  {section.name}
                </Typography>
              }
            >
              {section.list.map((link) => {
                return (
                  <ListItem
                    disablePadding
                    divider
                    key={'navigation-main-pages' + link.name}
                    component={RouterLink}
                    to={link.path}
                    onClick={handleClose}
                    sx={{
                      ...listItemStyles,
                      paddingX: isMobile ? 2 : 0,
                    }}
                  >
                    <ListItemText primary={link.name} />
                  </ListItem>
                );
              })}
            </MenuList>
          </Grid>
        );
      })}
      <Grid size={{ xs: 12, md: 4 }}>
        <MenuList
          subheader={
            <Typography
              variant='h3'
              sx={{
                paddingX: isMobile ? 2 : 0,
                paddingBottom: isMobile ? 1 : 2,
              }}
              id='nested-list-subheader'
            >
              {t('header.menu.legalInsights')}
            </Typography>
          }
        >
          {externalLegalInsightsLinks.map((link) => {
            return (
              <ListItem
                key={'navigation-alma-insights-' + link.id}
                secondaryAction={<ArrowOutwardIcon fontSize='medium' />}
                disablePadding
                component={Link}
                href={link.href}
                target='_blank'
                divider
                rel='noopener noreferrer'
                underline='none'
                sx={{ ...listItemStyles, paddingX: isMobile ? 2 : 0 }}
                onClick={handleClose}
              >
                <ListItemText primary={link.label} />
              </ListItem>
            );
          })}
        </MenuList>
      </Grid>
    </Grid>
  );
}

interface NavigationMenuProps {
  readonly minimize?: boolean;
}

export default function NavigationMenu({ minimize }: NavigationMenuProps) {
  const { t } = useTranslation();
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // Close menu when user navigates elsewhere
    setAnchorElement(null);
  }, [location]);

  const open = Boolean(anchorElement);

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElement(null);
  };

  return (
    <Box>
      {minimize ? (
        <IconButton
          color='primary'
          size='large'
          sx={{ color: 'text.secondary' }}
          onClick={handleButtonClick}
          aria-label={t('header.menu.title')}
        >
          <MenuIcon />
        </IconButton>
      ) : (
        <Button
          id='menu-button'
          variant='text'
          size='small'
          sx={{
            color: 'text.primary',
            fontSize: '14px',
          }}
          onClick={handleButtonClick}
          disableElevation
          endIcon={<MenuIcon />}
        >
          {t('header.menu.title')}
        </Button>
      )}
      {open && isMobile && (
        <SwipeableDrawer
          anchor={'right'}
          open={open}
          onClose={handleClose}
          onOpen={() => {}}
          hysteresis={0.5}
          disableSwipeToOpen
          sx={{ zIndex: 1301 }}
          slotProps={{
            paper: {
              sx: { maxWidth: '400px' },
            },
          }}
        >
          <Stack
            direction='row'
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: 2,
            }}
          >
            <Typography variant='h2'>{t('header.menu.title')}</Typography>
            <IconButton size='large' onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          <NavList handleClose={handleClose} />
        </SwipeableDrawer>
      )}
      {open && !isMobile && (
        <Dialog
          fullScreen
          open={open}
          onClose={handleClose}
          sx={{ zIndex: 1301 }}
        >
          <Paper elevation={4}>
            <Container maxWidth='lg' disableGutters sx={{ paddingX: 2 }}>
              <Box
                sx={{
                  height: '48px',
                }}
              >
                <Stack
                  direction='row'
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <Typography
                    component='h2'
                    sx={{
                      fontWeight: 600,
                      fontSize: '18px',
                      lineHeight: '130%',
                    }}
                  >
                    {t('header.menu.title')}
                  </Typography>
                  {minimize ? (
                    <IconButton
                      sx={{ padding: 1.5, marginRight: -2 }}
                      onClick={() => handleClose()}
                    >
                      <CloseIcon />
                    </IconButton>
                  ) : (
                    <Button
                      variant='text'
                      size='small'
                      disableElevation
                      sx={{
                        fontSize: '14px',
                        color: 'text.primary',
                        marginRight: -2,
                      }}
                      endIcon={<CloseIcon />}
                      onClick={() => handleClose()}
                    >
                      {t('header.menu.close')}
                    </Button>
                  )}
                </Stack>
              </Box>
            </Container>
          </Paper>
          <Container
            maxWidth='lg'
            disableGutters
            sx={{
              paddingX: isMobile ? 0 : 2,
              marginTop: { xs: 4, md: 8 },
              marginBottom: 8,
            }}
          >
            <NavList handleClose={handleClose} />
          </Container>
        </Dialog>
      )}
    </Box>
  );
}
